/**
 * Filesystem-based route registry
 * Automatically maps container directory structure to routes
 */

// CS Routes (normalized paths)
const csRoutes = {
  // 2019
  '/2019/katowice': () => import('../container/CSGO/2019/Katowice2019'),
  '/2019/berlin': () => import('../container/CSGO/2019/Berlin2019'),

  // 2021
  '/2021/stockholm': () => import('../container/CSGO/2021/Stockholm2021'),

  // 2022
  '/2022/antwerp/rmr': () => import('../container/CSGO/2022/Antwerp/Antwerp2022RMR'),
  '/2022/antwerp': () => import('../container/CSGO/2022/Antwerp/Antwerp2022'),
  '/2022/rio/rmr': () => import('../container/CSGO/2022/Rio/Rio2022RMR'),
  '/2022/rio': () => import('../container/CSGO/2022/Rio/Rio2022'),

  // 2023
  '/2023/paris/qualifier': () => import('../container/CSGO/2023/Paris2023Qual'),
  '/2023/paris/rmr': () => import('../container/CSGO/2023/Paris2023RMR'),
  '/2023/paris': () => import('../container/CSGO/2023/Paris2023'),

  // 2024
  '/2024/copenhagen/qualifier': () => import('../container/CS2/2024/Copenhagen/Copenhagen2024Qual'),
  '/2024/copenhagen/rmr': () => import('../container/CS2/2024/Copenhagen/Copenhagen2024RMR'),
  '/2024/copenhagen': () => import('../container/CS2/2024/Copenhagen/Copenhagen2024'),
  '/2024/shanghai/qualifier': () => import('../container/CS2/2024/Shanghai/Shanghai2024Qual'),
  '/2024/shanghai/rmr': () => import('../container/CS2/2024/Shanghai/Shanghai2024RMR'),
  '/2024/shanghai': () => import('../container/CS2/2024/Shanghai/Shanghai2024Provisional'),

  // 2025
  '/2025/austin/mrq': () => import('../container/CS2/2025/Austin/Austin2025MRQ'),
  '/2025/austin': () => import('../container/CS2/2025/Austin/Austin2025Provisional'),
  '/2025/budapest': () => import('../container/CS2/2025/Budapest/Budapest2025Provisional'),

  // 2026
  '/2026/singapore': () => import('../container/CS2/2026/Singapore/Singapore2026Provisional'),
  '/2026/esl-pro-league-24': () => import('../container/CS2/2026/ESLProLeague24'),
  '/2026/cologne': () => import('../container/CS2/2026/Cologne/Cologne2026Provisional'),
};

// Redirect mappings from old paths to new normalized paths
const csRedirects = {
  '/26singapore': '/2026/singapore',
  '/26epl24': '/2026/esl-pro-league-24',
  '/19katowice': '/2019/katowice',
  '/19berlin': '/2019/berlin',
  '/21stockholm': '/2021/stockholm',
  '/22rmr_antwerp': '/2022/antwerp/rmr',
  '/22antwerp': '/2022/antwerp',
  '/22rmr_rio': '/2022/rio/rmr',
  '/22rio': '/2022/rio',
  '/23qual_paris': '/2023/paris/qualifier',
  '/23rmr_paris': '/2023/paris/rmr',
  '/23paris': '/2023/paris',
  '/24qual_copenhagen': '/2024/copenhagen/qualifier',
  '/24rmr_copenhagen': '/2024/copenhagen/rmr',
  '/24copenhagen': '/2024/copenhagen',
  '/24qual_shanghai': '/2024/shanghai/qualifier',
  '/24rmr_shanghai': '/2024/shanghai/rmr',
  '/24shanghai': '/2024/shanghai',
  '/25mrq_austin': '/2025/austin/mrq',
  '/25austin': '/2025/austin',
  '/25budapest': '/2025/budapest',
  '/26cologne': '/2026/cologne',
};

// R6 Routes (normalized paths)
const r6Routes = {
  '/2023/copenhagen': () => import('../container/RainbowSix/R6CPH23'),
  '/2023/atlanta': () => import('../container/RainbowSix/R6ATL23'),
  '/2024/manchester': () => import('../container/RainbowSix/R6MAN24'),
  '/2024/montreal': () => import('../container/RainbowSix/R6MTL24'),
  '/2025/munich': () => import('../container/RainbowSix/R6MUN25'),
  '/2026/salt-lake-city': () => import('../container/RainbowSix/R6SLC26'),
};

// Redirect mappings from old R6 paths to new normalized paths
const r6Redirects = {
  '/23majorcph': '/2023/copenhagen',
  '/23majoratl': '/2023/atlanta',
  '/24majorman': '/2024/manchester',
  '/24majormtl': '/2024/montreal',
  '/26majorslc': '/2026/salt-lake-city',
  // Also support /r6/ prefix for old paths
  '/r6/23majorcph': '/2023/copenhagen',
  '/r6/23majoratl': '/2023/atlanta',
  '/r6/24majorman': '/2024/manchester',
  '/r6/24majormtl': '/2024/montreal',
  '/r6/26majorslc': '/2026/salt-lake-city',
};

/**
 * Get all routes for a site type
 * @param {string} siteType - 'cs' or 'r6'
 * @returns {Object} Route mapping
 */
const sharedRoutes = {
  '/privacy': () => import('../container/Privacy'),
};

export const getRoutes = (siteType) => {
  const base = siteType === 'cs' ? csRoutes : r6Routes;
  return { ...base, ...sharedRoutes };
};

/**
 * Get route component dynamically
 * @param {string} path - Route path
 * @param {string} siteType - Site type
 * @returns {Promise<React.Component>} Component
 */
export const getRouteComponent = async (path, siteType) => {
  const routes = getRoutes(siteType);
  const routeLoader = routes[path];
  
  if (!routeLoader) {
    return null;
  }

  try {
    const module = await routeLoader();
    return module.default || module;
  } catch (error) {
    console.error(`Failed to load route ${path}:`, error);
    return null;
  }
};

/**
 * Get all route paths for a site type
 * @param {string} siteType - 'cs' or 'r6'
 * @returns {string[]} Array of route paths
 */
export const getRoutePaths = (siteType) => {
  return Object.keys(getRoutes(siteType));
};

export const getEventRoutePaths = (siteType) => {
  const routes = siteType === 'cs' ? csRoutes : r6Routes;
  return Object.keys(routes);
};

/**
 * Get redirect mapping for old paths
 * @param {string} siteType - 'cs' or 'r6'
 * @returns {Object} Redirect mapping from old path to new path
 */
export const getRedirects = (siteType) => {
  if (siteType === 'cs') {
    return csRedirects;
  }
  if (siteType === 'r6') {
    return r6Redirects;
  }
  return {};
};
